import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { AppIcon } from "../components/AppIcon";
import { Icon } from "../components/Icon";
import { Page, TopBar } from "../components/Layout";
import { Sheet } from "../components/Sheet";
import { WidgetBody, WidgetTile, useWidgetData, type Size } from "../components/Widget";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { greeting } from "../lib/format";
import { dims, moveTo, pack, rowsUsed } from "../lib/grid";
import { useToast } from "../lib/toast";
import type { AppCard } from "../lib/types";

interface Item {
  id: string;
  app_id: string;
  kind: "icon" | "widget";
  widget_id: string | null;
  size: string;
  x: number;
  y: number;
}

interface SpaceData {
  items: Item[];
  apps: Record<string, AppCard>;
  max_items: number;
}

export function Space() {
  const { user } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [space, setSpace] = useState<SpaceData | null>(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState<string | null>(null); // app id, "" = choose
  const [searching, setSearching] = useState(false);

  const load = useCallback(() => api<SpaceData>("/api/space").then(setSpace), []);
  useEffect(() => {
    load();
  }, [load]);

  // /?add=<app> (from an app's menu) opens the add sheet for that app.
  useEffect(() => {
    const add = params.get("add");
    if (add && space?.apps[add]) {
      setEditing(true);
      setAdding(add);
      params.delete("add");
      setParams(params, { replace: true });
    }
  }, [params, space, setParams]);

  const save = async (items: Item[]) => {
    setSpace((s) => (s ? { ...s, items } : s));
    try {
      setSpace(await api<SpaceData>("/api/space", { method: "PUT", body: { items } }));
    } catch (e) {
      toast((e as Error).message, "error");
      load();
    }
  };

  const open = (appId: string, path?: string) => nav(`/apps/${appId}${path ?? ""}`);
  const apps = space ? Object.values(space.apps) : [];
  const items = space?.items ?? [];
  const noApps = space !== null && apps.length === 0;
  const startAdding = () => {
    setEditing(true);
    setAdding(""); // start at the app list, even with a single app installed
  };

  return (
    <>
      {editing ? (
        <TopBar title="Edit space" avatar={false}>
          <button className="link-btn" onClick={() => setEditing(false)}>Done</button>
        </TopBar>
      ) : (
        <TopBar title="Space">
          {apps.length > 0 && (
            <button className="icon-btn" aria-label="Search your apps" onClick={() => setSearching(true)}>
              <Icon name="search" />
            </button>
          )}
        </TopBar>
      )}
      <Page>
        {!editing && user && <span className="muted" style={{ fontSize: 15 }}>{greeting(user.display_name)}</span>}
        {editing && <p className="small muted">Drag tiles to move them. Other tiles make room. Tap − to remove.</p>}

        {noApps && (
          <div className="space">
            <div className="space-grid">
              <Link to="/marketplace" className="cell add"><Icon name="plus" />Add</Link>
              {Array.from({ length: 7 }, (_, i) => <div key={i} className="cell" aria-hidden />)}
            </div>
          </div>
        )}
        {space && !noApps && (
          <SpaceGrid
            items={items}
            apps={space.apps}
            editing={editing}
            onEdit={() => setEditing(true)}
            onChange={save}
            onOpen={open}
            onAdd={startAdding}
          />
        )}

        {noApps && (
          <>
            <div className="stack-sm">
              <h2 style={{ fontSize: 22 }}>Nothing here yet</h2>
              <p className="muted" style={{ fontSize: 15, lineHeight: 1.5 }}>
                Install an app, then place its icon or a widget on this grid. You sign in once for all of them, and they only share data when you say so.
              </p>
            </div>
            <div className="stack" style={{ marginTop: "auto" }}>
              <Link className="btn btn-primary btn-block" to="/marketplace">Browse the marketplace</Link>
              <Link className="btn btn-block" to="/people">Invite someone</Link>
            </div>
          </>
        )}

        {!noApps && space && !editing && (
          <button className="link-btn" style={{ alignSelf: "center", padding: "0 14px" }} onClick={() => setEditing(true)}>
            <Icon name="layout" size={18} />Edit space
          </button>
        )}
        {editing && (
          <button className="btn btn-block" onClick={startAdding}>
            <Icon name="plus" />Add icon or widget
          </button>
        )}
      </Page>

      {adding !== null && space && (
        <AddSheet
          apps={apps}
          initial={adding}
          full={items.length >= space.max_items}
          onClose={() => setAdding(null)}
          onAdd={(item) => {
            // New tiles go to the first free cells that fit; the user can drag them after.
            const [placed] = pack([...items, { ...item, id: "new", x: null, y: null }]).slice(-1);
            save([...items, { ...item, x: placed.x, y: placed.y }]);
            setAdding(null);
          }}
        />
      )}
      {searching && <SearchSheet apps={apps} onClose={() => setSearching(false)} onOpen={(id) => open(id)} />}
    </>
  );
}

const GAP = 10;
const LONG_PRESS_MS = 450;

interface Drag {
  id: string;
  pointerId: number;
  sx: number; // pointer start
  sy: number;
  offX: number; // pointer offset inside the tile
  offY: number;
  active: boolean;
  px: number; // pointer now
  py: number;
  x: number; // target cell
  y: number;
}

/**
 * The grid itself. Tiles sit on measured cells (absolute positions, so moves
 * animate). In edit mode a tile follows the pointer and the others make room
 * live (moveTo); dropping saves the positions. Long-pressing a tile outside
 * edit mode enters it and keeps dragging with the same finger. Keyboard: arrow
 * keys move the focused tile, Delete removes it.
 */
function SpaceGrid({ items, apps, editing, onEdit, onChange, onOpen, onAdd }: {
  items: Item[];
  apps: Record<string, AppCard>;
  editing: boolean;
  onEdit: () => void;
  onChange: (items: Item[]) => void;
  onOpen: (appId: string, path?: string) => void;
  onAdd: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(358);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [announce, setAnnounce] = useState("");
  const dragRef = useRef<Drag | null>(null);
  const timer = useRef<number | null>(null);
  const editingRef = useRef(editing);
  const suppressClick = useRef(false);
  editingRef.current = editing;
  dragRef.current = drag;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const cell = (width - 3 * GAP) / 4;
  const pitch = cell + GAP;
  const layout = useMemo(
    () => (drag?.active ? moveTo(items, drag.id, drag.x, drag.y) : items),
    [items, drag?.active, drag?.id, drag?.x, drag?.y],
  );
  const rows = Math.max(rowsUsed(layout) + (editing ? 1 : 0), editing ? 3 : 1);
  // Outside edit mode the Add tile takes the first free cell.
  const addCell = useMemo(() => {
    const [p] = pack([...items, { id: "add", size: "1x1", x: null, y: null }]).slice(-1);
    return p;
  }, [items]);
  const height = Math.max(rows, editing ? rows : addCell.y + 1) * pitch - GAP;

  const endDrag = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    const d = dragRef.current;
    if (d?.active) {
      const next = moveTo(items, d.id, d.x, d.y);
      const moved = next.find((i) => i.id === d.id)!;
      setAnnounce(`Moved to row ${moved.y + 1}, column ${moved.x + 1}`);
      onChange(next);
      suppressClick.current = true;
    }
    setDrag(null);
  }, [items, onChange]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      const dist = Math.hypot(e.clientX - d.sx, e.clientY - d.sy);
      if (!editingRef.current) {
        // Moving before the long-press fires means the user is scrolling.
        if (dist > 8) {
          if (timer.current) window.clearTimeout(timer.current);
          timer.current = null;
          setDrag(null);
        }
        return;
      }
      if (!d.active && dist < 6) return;
      const grid = ref.current!.getBoundingClientRect();
      const it = items.find((i) => i.id === d.id)!;
      const [w] = dims(it.size);
      const x = Math.max(0, Math.min(4 - w, Math.round((e.clientX - grid.left - d.offX) / pitch)));
      const y = Math.max(0, Math.round((e.clientY - grid.top - d.offY) / pitch));
      setDrag({ ...d, active: true, px: e.clientX, py: e.clientY, x, y });
      // Scroll when dragging near the top or bottom edge.
      if (e.clientY > window.innerHeight - 110) window.scrollBy(0, 14);
      else if (e.clientY < 130) window.scrollBy(0, -14);
    };
    // Keep the page from scrolling while a tile is being dragged (touch).
    const onTouchMove = (e: TouchEvent) => {
      if (dragRef.current && editingRef.current) e.preventDefault();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [items, pitch, endDrag]);

  const onPointerDown = (e: React.PointerEvent, item: Item) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest(".edit-x")) return;
    suppressClick.current = false;
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setDrag({ id: item.id, pointerId: e.pointerId, sx: e.clientX, sy: e.clientY, offX: e.clientX - r.left, offY: e.clientY - r.top, active: false, px: e.clientX, py: e.clientY, x: item.x, y: item.y });
    if (!editing) {
      timer.current = window.setTimeout(() => {
        timer.current = null;
        suppressClick.current = true;
        navigator.vibrate?.(10);
        onEdit(); // the same pointer keeps dragging
      }, LONG_PRESS_MS);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent, item: Item) => {
    const delta: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (delta[e.key]) {
      e.preventDefault();
      const [dx, dy] = delta[e.key];
      const next = moveTo(items, item.id, item.x + dx, item.y + dy);
      const moved = next.find((i) => i.id === item.id)!;
      setAnnounce(`Row ${moved.y + 1}, column ${moved.x + 1}`);
      onChange(next);
    } else if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      onChange(items.filter((i) => i.id !== item.id));
    }
  };

  const box = (x: number, y: number, size: string) => {
    const [w, h] = dims(size);
    return { left: x * pitch, top: y * pitch, width: w * cell + (w - 1) * GAP, height: h * cell + (h - 1) * GAP };
  };

  return (
    <div className={`space${editing ? " editing" : ""}${drag?.active ? " dragging-any" : ""}`}>
      <div ref={ref} className="space-canvas" style={{ height }} onContextMenu={(e) => e.preventDefault()}>
        {editing &&
          Array.from({ length: rows * 4 }, (_, i) => (
            <div key={`c${i}`} className="drop-cell" style={box(i % 4, Math.floor(i / 4), "1x1")} aria-hidden />
          ))}
        {drag?.active && (() => {
          const it = layout.find((i) => i.id === drag.id)!;
          return <div className="drop-target" style={box(it.x, it.y, it.size)} aria-hidden />;
        })()}
        {layout.map((item) => {
          const app = apps[item.app_id];
          if (!app) return null;
          const dragging = drag?.active && drag.id === item.id;
          const pos = box(item.x, item.y, item.kind === "icon" ? "1x1" : item.size);
          const style: React.CSSProperties = { ...pos };
          if (dragging) {
            const grid = ref.current!.getBoundingClientRect();
            style.transform = `translate(${drag!.px - grid.left - drag!.offX - pos.left}px, ${drag!.py - grid.top - drag!.offY - pos.top}px) scale(1.04)`;
          }
          const label = item.kind === "icon" ? `${app.name} icon` : `${app.name} widget, ${item.size.replace("x", " by ")}`;
          const tile =
            item.kind === "icon" ? (
              <button
                className={`app-tile${app.installation?.entitled === false ? " dim" : ""}`}
                tabIndex={editing ? -1 : 0}
                onClick={() => (suppressClick.current ? (suppressClick.current = false) : onOpen(app.id))}
              >
                <AppIcon app={app} size={Math.min(56, Math.round(cell * 0.68))} />
                <span>{app.name}</span>
              </button>
            ) : (
              <WidgetTile
                app={app}
                appId={app.id}
                widgetId={item.widget_id!}
                size={item.size as Size}
                tabIndex={editing ? -1 : 0}
                onOpen={(p) => (suppressClick.current ? (suppressClick.current = false) : onOpen(app.id, p))}
              />
            );
          return (
            <div
              key={item.id}
              className={`slot${dragging ? " dragging" : ""}`}
              style={style}
              onPointerDown={(e) => onPointerDown(e, item)}
              {...(editing
                ? { role: "button", tabIndex: 0, "aria-label": `${label}, row ${item.y + 1}, column ${item.x + 1}. Arrow keys move it, Delete removes it.`, onKeyDown: (e: React.KeyboardEvent) => onKeyDown(e, item) }
                : {})}
            >
              {tile}
              {editing && (
                <button className="edit-x" aria-label={`Remove ${label}`} onClick={() => onChange(items.filter((x) => x.id !== item.id))}>
                  <Icon name="minus" size={16} />
                </button>
              )}
            </div>
          );
        })}
        {!editing && (
          <div className="slot" style={box(addCell.x, addCell.y, "1x1")}>
            <button className="app-tile" onClick={onAdd}>
              <span className="app-icon add" style={{ width: Math.min(56, Math.round(cell * 0.68)), height: Math.min(56, Math.round(cell * 0.68)) }}><Icon name="plus" /></span>
              <span style={{ color: "var(--omni-accent)" }}>Add</span>
            </button>
          </div>
        )}
      </div>
      <span className="sr-only" aria-live="polite">{announce}</span>
    </div>
  );
}

const SIZE_LABEL: Record<string, { label: string; sub: string }> = {
  "1x1": { label: "Icon", sub: "1×1" },
  "2x1": { label: "2×1", sub: "half, 1 row" },
  "2x2": { label: "2×2", sub: "half, 2 rows" },
  "2x3": { label: "2×3", sub: "half, 3 rows" },
  "4x1": { label: "4×1", sub: "full, 1 row" },
  "4x2": { label: "4×2", sub: "full, 2 rows" },
  "4x3": { label: "4×3", sub: "full, 3 rows" },
};

function AddSheet({ apps, initial, full, onClose, onAdd }: { apps: AppCard[]; initial: string; full: boolean; onClose: () => void; onAdd: (item: Omit<Item, "x" | "y">) => void }) {
  // Two steps: pick one of your apps, then its icon or one of its widgets.
  // `initial` (from an app's own "Add to space") skips straight to step two.
  const [appId, setAppId] = useState(initial);
  const app = apps.find((a) => a.id === appId);
  if (!app) return <AppPicker apps={apps} onClose={onClose} onPick={setAppId} />;
  return <AppPlacements key={app.id} app={app} full={full} onClose={onClose} onAdd={onAdd} onBack={() => setAppId("")} />;
}

function AppPicker({ apps, onClose, onPick }: { apps: AppCard[]; onClose: () => void; onPick: (id: string) => void }) {
  const [q, setQ] = useState("");
  // ADR-0009: installed apps are user data, so the list is searchable.
  const found = apps.filter((a) => `${a.name} ${a.category}`.toLowerCase().includes(q.toLowerCase()));
  const widgetCount = (a: AppCard) => a.widgets?.length ?? 0;
  return (
    <Sheet label="Add to your space" onClose={onClose}>
      <span className="stack" style={{ gap: 0 }}>
        <span className="d" style={{ fontSize: 19, fontWeight: 700 }}>Add to your space</span>
        <span className="small muted">Choose an app, then its icon or a widget</span>
      </span>
      <label className="search-box">
        <Icon name="search" size={20} />
        <input type="search" placeholder="Search your apps" aria-label="Search your apps" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <div className="card flush">
        {found.map((a) => (
          <button key={a.id} className="list-row" onClick={() => onPick(a.id)}>
            <AppIcon app={a} size={40} />
            <span className="grow">
              {a.name}
              <span className="sub">{widgetCount(a) ? `Icon · ${widgetCount(a)} ${widgetCount(a) === 1 ? "widget" : "widgets"}` : "Icon"}</span>
            </span>
            <Icon name="chevron-right" className="trail" size={20} />
          </button>
        ))}
        {found.length === 0 && <p className="muted small" style={{ padding: 16 }}>No apps match.</p>}
      </div>
      <Link className="btn btn-block" to="/marketplace" onClick={onClose}><Icon name="plus" />Get more apps</Link>
    </Sheet>
  );
}

function AppPlacements({ app, full, onClose, onAdd, onBack }: { app: AppCard; full: boolean; onClose: () => void; onAdd: (item: Omit<Item, "x" | "y">) => void; onBack: () => void }) {
  // Every placement the app offers: its icon, then each widget × size.
  const options = useMemo(() => {
    const out: { key: string; widget: string | null; size: string; title: string }[] = [{ key: "icon", widget: null, size: "1x1", title: "Icon" }];
    for (const w of app.widgets ?? []) for (const s of w.sizes) out.push({ key: `${w.id}:${s}`, widget: w.id, size: s, title: w.title });
    return out;
  }, [app]);
  const widgets = app.widgets ?? [];
  const [widgetId, setWidgetId] = useState<string | null>(widgets[0]?.id ?? null);
  const [key, setKey] = useState(options.find((o) => o.widget && o.size === (widgets[0]?.default_size ?? "2x2"))?.key ?? "icon");
  const chosen = options.find((o) => o.key === key) ?? options[0];
  const sizes = options.filter((o) => o.widget === null || o.widget === widgetId);

  return (
    <Sheet label={`Add ${app.name}`} onClose={onClose}>
      <div className="row">
        <button className="icon-btn" aria-label="All apps" onClick={onBack}><Icon name="chevron-left" size={22} /></button>
        <AppIcon app={app} size={40} />
        <span className="grow stack" style={{ gap: 0 }}>
          <span className="d" style={{ fontSize: 19, fontWeight: 700 }}>{app.name}</span>
          <span className="small muted">{widgets.length ? "Pick the icon or a widget and its size" : "This app has no widgets yet"}</span>
        </span>
      </div>
      {widgets.length > 1 && (
        <div className="chips" role="radiogroup" aria-label="Widget">
          {widgets.map((w) => (
            <button key={w.id} role="radio" aria-checked={w.id === widgetId} className={`chip${w.id === widgetId ? " on" : ""}`}
              onClick={() => { setWidgetId(w.id); setKey(`${w.id}:${w.default_size ?? w.sizes[0]}`); }}>
              {w.title}
            </button>
          ))}
        </div>
      )}
      <div className="size-grid" role="radiogroup" aria-label="Size">
        {sizes.map((o) => (
          <button key={o.key} role="radio" aria-checked={o.key === key} className={`size-btn${o.key === key ? " on" : ""}`} onClick={() => setKey(o.key)}>
            {SIZE_LABEL[o.size].label}
            <small>{SIZE_LABEL[o.size].sub}</small>
          </button>
        ))}
      </div>
      <Preview app={app} widget={chosen.widget} size={chosen.size} />
      {full && <p className="small error">Your space is full. Remove something first.</p>}
      <button
        className="btn btn-primary btn-block"
        disabled={full}
        onClick={() => onAdd({ id: "", app_id: app.id, kind: chosen.widget ? "widget" : "icon", widget_id: chosen.widget, size: chosen.size })}
      >
        {chosen.widget ? `Add ${SIZE_LABEL[chosen.size].label} widget` : "Add icon to space"}
      </button>
    </Sheet>
  );
}

function Preview({ app, widget, size }: { app: AppCard; widget: string | null; size: string }) {
  const { data, error } = useWidgetData(app.id, widget ?? "", size, !!widget);
  return (
    <div className="space">
      <div className="space-grid preview-grid" aria-label="Preview">
        {Array.from({ length: 12 }, (_, i) => <div key={i} className="pcell" />)}
        <div className="overlay space-grid">
          {widget ? (
            <div className={`tile span-${size}${size === "4x1" ? " action" : size.endsWith("x1") ? " row1" : ""}`}>
              <WidgetBody app={app} size={size as Size} data={data} error={error} />
            </div>
          ) : (
            <div className="app-tile span-1x1">
              <span style={{ borderRadius: 16, boxShadow: "0 0 0 2px var(--omni-accent)", display: "inline-flex" }}><AppIcon app={app} size={56} /></span>
              <span>{app.name}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SearchSheet({ apps, onClose, onOpen }: { apps: AppCard[]; onClose: () => void; onOpen: (id: string) => void }) {
  const [q, setQ] = useState("");
  const found = apps.filter((a) => `${a.name} ${a.description}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Sheet label="Search your apps" onClose={onClose}>
      <label className="search-box">
        <Icon name="search" size={20} />
        <input autoFocus type="search" placeholder="Search your apps" aria-label="Search your apps" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      <div className="card flush">
        {found.map((a) => (
          <button key={a.id} className="list-row" onClick={() => onOpen(a.id)}>
            <AppIcon app={a} size={40} />
            <span className="grow">{a.name}<span className="sub">{a.category}</span></span>
            <Icon name="chevron-right" className="trail" size={20} />
          </button>
        ))}
        {found.length === 0 && <p className="muted small" style={{ padding: 16 }}>No apps match.</p>}
      </div>
    </Sheet>
  );
}
